import { Fragment, useEffect, useRef, useState } from "react";
import { Navigate, NavLink, Outlet, useLocation, useNavigate } from "react-router";
import { useSupportSummary } from "../api/support";
import { useAuth } from "../auth/AuthContext";
import { can, type Permission } from "../auth/permissions";
import { API_BASE, API_MODE } from "../env";
import { clearMockStorage } from "../mocks/keys";
import styles from "./layout.module.css";
import { Button, Dialog, ErrorState, RoleChip } from "./ui";

export const NAV = [
  { to: "/", label: "Dashboard", key: "d", end: true },
  { to: "/puzzles", label: "Puzzles", key: "p" },
  { to: "/schedule", label: "Schedule", key: "s" },
  { to: "/studio", label: "Studio", key: "k" },
  { to: "/levels", label: "Levels", key: "l" },
  { to: "/players", label: "Players", key: "u" },
  { to: "/teams", label: "Teams", key: "e" },
  { to: "/divisions", label: "Divisions", key: "v" },
  { to: "/arena", label: "Arena", key: "n" },
  { to: "/duels", label: "Duels", key: "x" },
  { to: "/rooms", label: "Rooms", key: "o" },
  { to: "/tournaments", label: "Tournaments", key: "2" },
  { to: "/reports", label: "Reports", key: "r" },
  { to: "/leaderboards", label: "Leaderboards", key: "t" },
  { to: "/integrity", label: "Integrity", key: "i" },
  { to: "/badges", label: "Badges", key: "b" },
  { to: "/monetisation", label: "Monetisation", key: "m" },
  { to: "/notifications", label: "Notifications", key: "h" },
  { to: "/banners", label: "Banners", key: "w" },
  { to: "/support", label: "Support", key: "q" },
  { to: "/learn", label: "Learn", key: "y" },
  { to: "/brain", label: "Brain cards", key: "f" },
  { to: "/automation", label: "Automation", key: "z" },
  { to: "/ask-lume", label: "Ask Lume", key: "1" },
  { to: "/engagement", label: "Engagement", key: "g" },
  { to: "/sharing", label: "Sharing", key: "4" },
  { to: "/app-updates", label: "App updates", key: "3" },
  { to: "/jobs", label: "Jobs", key: "j" },
  { to: "/config", label: "Config", key: "c" },
  { to: "/audit", label: "Audit", key: "a" },
] as const;

/**
 * Monetisation sub-pages, listed under "Monetisation" while it is open (contracts/ads.md §5, paywall.md §5,
 * revenue.md §5, web.md §5.7). Revenue, Customers and Events are open to every role; Web payments is hidden from
 * viewers (payments.view is editor and above).
 */
export const MONETISATION_NAV = [
  { to: "/monetisation", label: "Paywall", end: true },
  { to: "/monetisation/revenue", label: "Revenue", end: false },
  { to: "/monetisation/customers", label: "Customers", end: false },
  { to: "/monetisation/events", label: "Events", end: false },
  { to: "/monetisation/paywall", label: "Paywall settings", end: false },
  { to: "/monetisation/experiments", label: "Experiments", end: false },
  { to: "/monetisation/ads", label: "Ads", end: false },
  { to: "/monetisation/web-payments", label: "Web payments", end: false, permission: "payments.view" },
] as const satisfies readonly { to: string; label: string; end: boolean; permission?: Permission }[];

/** Tournaments sub-pages, listed under "Tournaments" while it is open (contracts/tournaments.md §9). */
export const TOURNAMENTS_NAV = [
  { to: "/tournaments", label: "Cups", end: true },
  { to: "/tournaments/puzzles", label: "Cup puzzles", end: false },
  { to: "/tournaments/prizes", label: "Prizes", end: false },
  { to: "/tournaments/settings", label: "Settings", end: false },
] as const;

/** Sharing sub-pages, listed under "Sharing" while it is open (contracts/index-cards.md §5). */
export const SHARING_NAV = [
  { to: "/sharing", label: "Overview", end: true },
  { to: "/sharing/settings", label: "Settings", end: false },
] as const;

/** Puzzle Studio sub-pages, listed under "Studio" while it is open (contracts/studio.md §4). */
export const STUDIO_NAV = [
  { to: "/studio", label: "Overview", end: true },
  { to: "/studio/new", label: "New job", end: false },
  { to: "/studio/jobs", label: "Jobs", end: false },
  { to: "/studio/bank", label: "Bank", end: false },
] as const;

export function EnvBadge() {
  return (
    <span
      className={styles.env}
      data-mode={API_MODE}
      title={API_MODE === "mock" ? "Mock API (MSW) — VITE_API_MODE=mock" : `Live API at ${API_BASE}`}
    >
      <span className={styles.envDot} aria-hidden />
      {API_MODE === "mock" ? "MOCK" : "LIVE"}
    </span>
  );
}

function isTyping(el: EventTarget | null) {
  const node = el as HTMLElement | null;
  return Boolean(node && (node.isContentEditable || ["INPUT", "TEXTAREA", "SELECT"].includes(node.tagName)));
}

/** "g" then a letter jumps between sections; "/" focuses the page search; "?" shows help. */
function useGlobalShortcuts(openHelp: () => void) {
  const navigate = useNavigate();
  const pendingG = useRef<number | null>(null);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.defaultPrevented || e.metaKey || e.ctrlKey || e.altKey || isTyping(e.target)) return;
      if (document.querySelector("[role=dialog]")) return;
      if (pendingG.current !== null) {
        window.clearTimeout(pendingG.current);
        pendingG.current = null;
        const item = NAV.find((n) => n.key === e.key.toLowerCase());
        if (item) {
          e.preventDefault();
          navigate(item.to);
        }
        return;
      }
      if (e.key === "g") {
        pendingG.current = window.setTimeout(() => (pendingG.current = null), 1200);
      } else if (e.key === "/") {
        const search = document.querySelector<HTMLInputElement>("[data-search]");
        if (search) {
          e.preventDefault();
          search.focus();
          search.select();
        }
      } else if (e.key === "?") {
        e.preventDefault();
        openHelp();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [navigate, openHelp]);
}

export function AppLayout() {
  const { admin, status, error, logout, refetch } = useAuth();
  const location = useLocation();
  const [help, setHelp] = useState(false);
  const openHelp = useRef(() => setHelp(true)).current;
  useGlobalShortcuts(openHelp);
  // Support conversations waiting on the team: the badge on "Support", every 30 s.
  const support = useSupportSummary(Boolean(admin));
  const waiting = support.data?.unread ?? 0;
  const sectionIndex = Math.max(
    0,
    NAV.findIndex((n) => (n.to === "/" ? location.pathname === "/" : location.pathname.startsWith(n.to))),
  );

  if (status === "loading") {
    return (
      <div className={styles.boot} role="status">
        <span className={styles.bootDot} />
        <span className="monoLabel">Checking session…</span>
      </div>
    );
  }
  if (status === "error") {
    return (
      <div className={styles.bootError}>
        <ErrorState error={error} onRetry={refetch} title="Can't reach the admin API" />
      </div>
    );
  }
  if (!admin) {
    const next = location.pathname + location.search;
    return <Navigate to={`/login${next !== "/" ? `?next=${encodeURIComponent(next)}` : ""}`} replace />;
  }

  return (
    <div className={styles.shell}>
      <a href="#main" className={styles.skip}>
        Skip to content
      </a>
      <aside className={styles.sidebar}>
        <div className={styles.brand}>
          <span className={styles.brandDot} aria-hidden />
          <span className={styles.brandName}>IQ Daily</span>
          <span className="monoLabel">admin</span>
        </div>
        <nav className={styles.nav} aria-label="Sections">
          {NAV.map((item, i) => (
            <Fragment key={item.to}>
              <NavLink to={item.to} end={"end" in item ? item.end : false} className={styles.navItem}>
                <span className={styles.navIndex}>{String(i + 1).padStart(2, "0")}</span>
                <span className={styles.navLabel}>
                  {item.label}
                  {item.to === "/support" && waiting > 0 ? (
                    <span className={styles.navBadge} aria-label={`${waiting} waiting`} data-testid="support-badge">
                      {waiting}
                    </span>
                  ) : null}
                </span>
                <kbd className={styles.navKbd} aria-hidden>
                  g {item.key}
                </kbd>
              </NavLink>
              {item.to === "/studio" && location.pathname.startsWith("/studio") ? (
                <div className={styles.subNav} role="group" aria-label="Studio pages">
                  {STUDIO_NAV.map((sub) => (
                    <NavLink key={sub.to} to={sub.to} end={sub.end} className={styles.subNavItem}>
                      {sub.label}
                    </NavLink>
                  ))}
                </div>
              ) : null}
              {item.to === "/tournaments" && location.pathname.startsWith("/tournaments") ? (
                <div className={styles.subNav} role="group" aria-label="Tournaments pages">
                  {TOURNAMENTS_NAV.map((sub) => (
                    <NavLink key={sub.to} to={sub.to} end={sub.end} className={styles.subNavItem}>
                      {sub.label}
                    </NavLink>
                  ))}
                </div>
              ) : null}
              {item.to === "/sharing" && location.pathname.startsWith("/sharing") ? (
                <div className={styles.subNav} role="group" aria-label="Sharing pages">
                  {SHARING_NAV.map((sub) => (
                    <NavLink key={sub.to} to={sub.to} end={sub.end} className={styles.subNavItem}>
                      {sub.label}
                    </NavLink>
                  ))}
                </div>
              ) : null}
              {item.to === "/monetisation" && location.pathname.startsWith("/monetisation") ? (
                <div className={styles.subNav} role="group" aria-label="Monetisation pages">
                  {MONETISATION_NAV.filter((sub) => !("permission" in sub) || can(admin.role, sub.permission)).map((sub) => (
                    <NavLink key={sub.to} to={sub.to} end={sub.end} className={styles.subNavItem}>
                      {sub.label}
                    </NavLink>
                  ))}
                </div>
              ) : null}
            </Fragment>
          ))}
        </nav>
        <div className={styles.sidebarFoot}>
          <button type="button" className={styles.helpButton} onClick={() => setHelp(true)}>
            <kbd>?</kbd> Keyboard shortcuts
          </button>
          <span className={styles.apiBase} title={API_BASE}>
            {API_MODE === "mock" ? "msw · in-browser" : API_BASE ? API_BASE.replace(/^https?:\/\//, "") : "same origin · /admin/v1"}
          </span>
          {API_MODE === "mock" ? (
            <button
              type="button"
              className={styles.helpButton}
              onClick={() => {
                if (window.confirm("Reset mock data? Your mock approvals, swaps and config edits are discarded and you are signed out.")) {
                  clearMockStorage();
                  window.location.assign("/login");
                }
              }}
            >
              Reset mock data
            </button>
          ) : null}
        </div>
      </aside>

      <div className={styles.main}>
        <header className={styles.topbar}>
          <div className={styles.crumbs}>
            <span className="monoLabel">{String(sectionIndex + 1).padStart(2, "0")}</span>
            <span className={styles.crumbTitle}>{NAV[sectionIndex]?.label}</span>
          </div>
          <div className={styles.topbarRight}>
            <EnvBadge />
            <span className={styles.email} title={`Signed in as ${admin.email}`}>
              {admin.email}
            </span>
            <RoleChip role={admin.role} />
            <Button size="sm" variant="ghost" onClick={() => void logout()}>
              Sign out
            </Button>
          </div>
        </header>
        <main id="main" className={styles.content} tabIndex={-1}>
          <Outlet />
        </main>
      </div>

      <Dialog open={help} title="Keyboard shortcuts" onClose={() => setHelp(false)} width={420}>
        <dl className={styles.shortcuts}>
          {NAV.map((n) => (
            <div key={n.key}>
              <dt>
                <kbd>g</kbd> <kbd>{n.key}</kbd>
              </dt>
              <dd>{n.label}</dd>
            </div>
          ))}
          <div>
            <dt>
              <kbd>/</kbd>
            </dt>
            <dd>Focus search</dd>
          </div>
          <div>
            <dt>
              <kbd>r</kbd>
            </dt>
            <dd>Reveal answer (puzzle and level previews)</dd>
          </div>
          <div>
            <dt>
              <kbd>←</kbd> <kbd>→</kbd>
            </dt>
            <dd>Previous / next level in a pack, or round in a match replay</dd>
          </div>
          <div>
            <dt>
              <kbd>Home</kbd> <kbd>End</kbd>
            </dt>
            <dd>First / last round in a match replay</dd>
          </div>
          <div>
            <dt>
              <kbd>Esc</kbd>
            </dt>
            <dd>Close dialogs and previews</dd>
          </div>
        </dl>
      </Dialog>
    </div>
  );
}
